import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Portal from "components/layout/Portal";
import DevNavActionPanel from "components/control/DevNavActionPanel";
import DevNavBehaviorPanel from "components/control/DevNavBehaviorPanel";
import DevNavRecoveryPanel from "components/control/DevNavRecoveryPanel";
import NavLogFilterBar from "components/control/NavLogFilterBar";
import NavLogFilterModeToggle from "components/control/NavLogFilterModeToggle";
import { ReactComponent as BottomRightCornerIcon } from "assets/svgs/navigation/map/bottomrightcorner.svg";
import { ReactComponent as DownTriangleIcon } from "assets/svgs/DownTriangle.svg";
import { navServiceLogLineClassName } from "lib/utils/navServiceLogLineStyle";
import { useDevNavLogs, useDevNavLogsScroll } from "providers/DevNavLogsProvider";
import { useTranslation } from "react-i18next";

const POPUP_GEOMETRY_STORAGE_KEY = "dev-nav-logs-popup-geometry";
const POPUP_TAB_STORAGE_KEY = "dev-nav-logs-popup-tab";
const POPUP_COLLAPSED_STORAGE_KEY = "dev-nav-logs-popup-collapsed";
const AUTO_SCROLL_RESUME_MS = 7000;
const MIN_W = 240;
const MIN_H = 160;
const DEFAULT_W = 420;
const DEFAULT_H = 280;
/** Keep the popup below the control top bar (`h-topBar` = 5rem). */
const TOP_BAR_CLEARANCE_PX = 88;

type PopupTab = "logs" | "actions" | "navigation" | "behavior";

const VALID_TABS: PopupTab[] = ["logs", "actions", "navigation", "behavior"];

const readStoredTab = (): PopupTab => {
    if (typeof window === "undefined") return "logs";
    try {
        const raw = window.localStorage.getItem(POPUP_TAB_STORAGE_KEY);
        if (raw === "recovery") {
            return "navigation";
        }
        return VALID_TABS.includes(raw as PopupTab) ? (raw as PopupTab) : "logs";
    } catch {
        return "logs";
    }
};

const readStoredCollapsed = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(POPUP_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
};

type PopupGeometry = {
    x: number;
    y: number;
    w: number;
    h: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const formatTwistAxis = (value: number) => value.toFixed(3);

const readStoredGeometry = (): PopupGeometry => {
    const margin = 16;
    const fallback: PopupGeometry = {
        x: margin,
        y: TOP_BAR_CLEARANCE_PX,
        w: DEFAULT_W,
        h: DEFAULT_H,
    };

    if (typeof window === "undefined") {
        return fallback;
    }

    try {
        const raw = window.localStorage.getItem(POPUP_GEOMETRY_STORAGE_KEY);
        if (!raw) {
            return {
                x: Math.max(margin, window.innerWidth - DEFAULT_W - margin),
                y: TOP_BAR_CLEARANCE_PX,
                w: DEFAULT_W,
                h: DEFAULT_H,
            };
        }

        const parsed = JSON.parse(raw) as Partial<PopupGeometry>;
        const w = clamp(Number(parsed.w) || DEFAULT_W, MIN_W, window.innerWidth - margin * 2);
        const h = clamp(Number(parsed.h) || DEFAULT_H, MIN_H, window.innerHeight - margin * 2);
        const maxX = Math.max(margin, window.innerWidth - w - margin);
        const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - margin);

        return {
            x: clamp(Number(parsed.x) || margin, margin, maxX),
            y: clamp(Number(parsed.y) || TOP_BAR_CLEARANCE_PX, TOP_BAR_CLEARANCE_PX, maxY),
            w,
            h,
        };
    } catch {
        return fallback;
    }
};

const DevNavLogsPopup: React.FC = () => {
    const { t } = useTranslation("control");
    const {
        popupEnabled,
        loading,
        error,
        paused,
        togglePaused,
        performMovement,
        logFilterDraft,
        setLogFilterDraft,
        logFilterItems,
        addLogFilterItem,
        removeLogFilterItem,
        logFilterMode,
        setLogFilterMode,
    } = useDevNavLogs();
    const { scrollRef, onScroll, clear, lines } = useDevNavLogsScroll({
        resumeDelayMs: AUTO_SCROLL_RESUME_MS,
    });
    const [geometry, setGeometry] = useState(readStoredGeometry);
    const [activeTab, setActiveTab] = useState<PopupTab>(readStoredTab);
    const [collapsed, setCollapsed] = useState(readStoredCollapsed);
    const panelRef = useRef<HTMLDivElement | null>(null);
    const portalTarget = useMemo(
        () => (typeof document !== "undefined" ? document.body : null),
        [],
    );

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(POPUP_GEOMETRY_STORAGE_KEY, JSON.stringify(geometry));
    }, [geometry]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(POPUP_TAB_STORAGE_KEY, activeTab);
    }, [activeTab]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        window.localStorage.setItem(POPUP_COLLAPSED_STORAGE_KEY, collapsed ? "true" : "false");
    }, [collapsed]);

    const onDragHandlePointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (e.button !== 0) return;
            // Keep tabs / Pause / Clear clickable; drag from the rest of the header.
            const target = e.target as Element | null;
            if (target?.closest?.("button, input, textarea, a, [role='tab']")) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();

            const originX = geometry.x;
            const originY = geometry.y;
            const startClientX = e.clientX;
            const startClientY = e.clientY;

            const onMove = (ev: PointerEvent) => {
                const margin = 8;
                const nx = originX + (ev.clientX - startClientX);
                const ny = originY + (ev.clientY - startClientY);
                const w = panelRef.current?.offsetWidth ?? geometry.w;
                const h = panelRef.current?.offsetHeight ?? geometry.h;
                const maxX = Math.max(margin, window.innerWidth - w - margin);
                const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - margin);

                setGeometry((prev) => ({
                    ...prev,
                    x: clamp(nx, margin, maxX),
                    y: clamp(ny, TOP_BAR_CLEARANCE_PX, maxY),
                }));
            };

            const onUp = () => {
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [geometry.h, geometry.w, geometry.x, geometry.y],
    );

    const onResizePointerDown = useCallback(
        (e: React.PointerEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const el = e.currentTarget;
            el.setPointerCapture(e.pointerId);
            const startX = e.clientX;
            const startY = e.clientY;
            const startW = geometry.w;
            const startH = geometry.h;

            const onMove = (ev: PointerEvent) => {
                const margin = 8;
                const maxW = Math.round(window.innerWidth * 0.92);
                const maxH = Math.round(window.innerHeight * 0.8);
                const dw = ev.clientX - startX;
                const dh = ev.clientY - startY;
                const w = clamp(startW + dw, MIN_W, maxW);
                const h = clamp(startH + dh, MIN_H, maxH);
                const maxX = Math.max(margin, window.innerWidth - w - margin);
                const maxY = Math.max(TOP_BAR_CLEARANCE_PX, window.innerHeight - h - margin);

                setGeometry((prev) => ({
                    w,
                    h,
                    x: clamp(prev.x, margin, maxX),
                    y: clamp(prev.y, TOP_BAR_CLEARANCE_PX, maxY),
                }));
            };

            const onUp = () => {
                try {
                    el.releasePointerCapture(e.pointerId);
                } catch {
                    /* ignore */
                }
                window.removeEventListener("pointermove", onMove);
                window.removeEventListener("pointerup", onUp);
                window.removeEventListener("pointercancel", onUp);
            };

            window.addEventListener("pointermove", onMove);
            window.addEventListener("pointerup", onUp);
            window.addEventListener("pointercancel", onUp);
        },
        [geometry.h, geometry.w],
    );

    if (!popupEnabled || !portalTarget) {
        return null;
    }

    return (
        <Portal visible target={portalTarget}>
            <div
                ref={panelRef}
                className="pointer-events-auto fixed z-[120] flex min-w-[15rem] flex-col overflow-hidden rounded-r10 border border-black80 bg-black90 shadow-lg"
                style={{
                    left: geometry.x,
                    top: geometry.y,
                    width: geometry.w,
                    height: collapsed ? "auto" : geometry.h,
                }}
            >
                <div
                    className="flex shrink-0 cursor-grab touch-none select-none items-center gap-2 border-b border-black80 bg-black100/90 px-3 py-2 active:cursor-grabbing"
                    onPointerDown={onDragHandlePointerDown}
                >
                    <div
                        className="flex min-w-0 flex-1 items-center gap-1"
                        role="tablist"
                        aria-label="Developer nav popup tabs"
                    >
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "logs"}
                            onClick={() => setActiveTab("logs")}
                            className={`rounded-r5 px-2 py-0.5 text-h5 ${
                                activeTab === "logs"
                                    ? "bg-black80 font-semibold text-white"
                                    : "text-black40 hover:bg-black80/60 hover:text-white"
                            }`}
                        >
                            {t("devNav.tabLogs", { defaultValue: "nav.service logs" })}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "actions"}
                            onClick={() => setActiveTab("actions")}
                            className={`rounded-r5 px-2 py-0.5 text-h5 ${
                                activeTab === "actions"
                                    ? "bg-black80 font-semibold text-white"
                                    : "text-black40 hover:bg-black80/60 hover:text-white"
                            }`}
                        >
                            {t("devNav.tabActions", { defaultValue: "Action panel" })}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "navigation"}
                            onClick={() => setActiveTab("navigation")}
                            className={`rounded-r5 px-2 py-0.5 text-h5 ${
                                activeTab === "navigation"
                                    ? "bg-black80 font-semibold text-white"
                                    : "text-black40 hover:bg-black80/60 hover:text-white"
                            }`}
                        >
                            {t("devNav.tabNavigation", { defaultValue: "Navigation" })}
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "behavior"}
                            onClick={() => setActiveTab("behavior")}
                            className={`rounded-r5 px-2 py-0.5 text-h5 ${
                                activeTab === "behavior"
                                    ? "bg-black80 font-semibold text-white"
                                    : "text-black40 hover:bg-black80/60 hover:text-white"
                            }`}
                        >
                            {t("devNav.tabBehavior", { defaultValue: "Behavior config" })}
                        </button>
                        {/* Flex spacer: primary drag surface in the header */}
                        <div className="min-h-[1.25rem] min-w-[0.75rem] flex-1" aria-hidden />
                    </div>
                    {activeTab === "logs" && !collapsed ? (
                        <>
                            <button
                                type="button"
                                onClick={togglePaused}
                                className="rounded-r5 border border-black70 bg-black90 px-2 py-0.5 text-h5 text-white hover:bg-black80"
                            >
                                {paused ? "Resume" : "Pause"}
                            </button>
                            <button
                                type="button"
                                onClick={clear}
                                className="rounded-r5 border border-black70 bg-black90 px-2 py-0.5 text-h5 text-white hover:bg-black80"
                            >
                                Clear
                            </button>
                        </>
                    ) : null}
                    <button
                        type="button"
                        aria-label={collapsed ? "Expand nav.service logs" : "Collapse nav.service logs"}
                        aria-expanded={!collapsed}
                        title={collapsed ? "Expand" : "Collapse"}
                        onClick={() => setCollapsed((prev) => !prev)}
                        className="rounded-r5 border border-black70 bg-black90 p-0.5 text-black60 hover:bg-black80 hover:text-white"
                    >
                        <DownTriangleIcon
                            className={`h-3.5 w-3.5 transition-transform${collapsed ? "" : " rotate-180"}`}
                        />
                    </button>
                </div>

                {!collapsed && (
                    activeTab === "logs" ? (
                    <>
                        <div className="flex shrink-0 items-start gap-2 border-b border-black80 bg-black100/80 px-3 py-1.5">
                            <NavLogFilterBar
                                draft={logFilterDraft}
                                onDraftChange={setLogFilterDraft}
                                items={logFilterItems}
                                onAddItem={addLogFilterItem}
                                onRemoveItem={removeLogFilterItem}
                                mode={logFilterMode}
                                compact
                            />
                            <NavLogFilterModeToggle
                                mode={logFilterMode}
                                onChange={setLogFilterMode}
                                className="mt-0.5"
                            />
                        </div>

                        <div className="shrink-0 border-b border-black80 bg-black100/80 px-3 py-1.5 font-mono text-[11px] tabular-nums text-black40">
                            <span className="text-black60">/perform/movement</span>
                            {performMovement ? (
                                <span className="ml-2 text-white">
                                    vx={formatTwistAxis(performMovement.linearX)}{" "}
                                    vy={formatTwistAxis(performMovement.linearY)}{" "}
                                    wz={formatTwistAxis(performMovement.angularZ)}{" "}
                                    {performMovement.hz.toFixed(1)} Hz
                                </span>
                            ) : (
                                <span className="ml-2">waiting…</span>
                            )}
                        </div>

                        {error ? (
                            <p className="shrink-0 px-3 py-2 text-h5 text-warning">{error}</p>
                        ) : null}

                        <div
                            ref={scrollRef}
                            onScroll={onScroll}
                            className="min-h-0 flex-1 overflow-y-auto bg-black100 p-3 font-mono text-[12px] leading-relaxed text-white"
                        >
                            {lines.length === 0 ? (
                                <p className="text-black40">
                                    {loading
                                        ? "Loading recent logs…"
                                        : logFilterItems.length > 0
                                          ? "No matching log lines."
                                          : "No log lines yet."}
                                </p>
                            ) : (
                                lines.map((line, index) => (
                                    <div
                                        key={`${index}-${line.slice(0, 32)}`}
                                        className={`whitespace-pre-wrap break-words${navServiceLogLineClassName(line)}`}
                                    >
                                        {line}
                                    </div>
                                ))
                            )}
                        </div>
                    </>
                ) : activeTab === "actions" ? (
                    <DevNavActionPanel />
                ) : activeTab === "navigation" ? (
                    <DevNavRecoveryPanel />
                ) : (
                    <DevNavBehaviorPanel />
                )
                )}

                {!collapsed && (
                    <button
                        type="button"
                        aria-label="Resize log popup"
                        className="pointer-events-auto absolute bottom-0 right-0 z-[1] flex h-10 w-10 cursor-nwse-resize touch-none translate-x-1.5 translate-y-1.5 items-center justify-center p-0 text-black60 hover:text-primary2"
                        onPointerDown={onResizePointerDown}
                    >
                        <BottomRightCornerIcon
                            className="pointer-events-none h-10 w-10 shrink-0 [&_path]:fill-current"
                            aria-hidden
                        />
                    </button>
                )}
            </div>
        </Portal>
    );
};

export default DevNavLogsPopup;
